import { Component, OnInit, Input, ChangeDetectionStrategy } from '@angular/core';
import { ModalController, AlertController } from '@ionic/angular/lazy';
import { ProductService } from 'src/app/services/product.service';
import { CartService } from 'src/app/services/cart.service';
import { ObserverService } from 'src/app/services/observer.service';
import { StorageService } from 'src/app/services/storage.service';
import { PermissionService } from 'src/app/services/permission.service';

@Component({
    selector: "app-select-product-variant",
    templateUrl: "./select-product-variant.page.html",
    styleUrls: ["./select-product-variant.page.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SelectProductVariantPage implements OnInit {

    cart = null;
    @Input() product = null;
    variants = null;
    show_sku = false;

    constructor(
        public modalController: ModalController,
        public alertController: AlertController,
        private cartService: CartService,
        private productService: ProductService,
        private observerService: ObserverService,
        private storageService: StorageService,
        public permissionService: PermissionService,
    ) { }

    ngOnInit() {

        this.storageService.get("show_sku").then((show_sku) => {
            this.show_sku = show_sku;
        })

        this.cartService.get().then((cart) => {

            this.cart = cart;

            // NOTE: load variants once the cart has loaded, as the sell price needs the cart (cart comes from native storage, which is slower than IndexedDB on Android)
            this.productService.getProductVariants(this.product.id).then((variants: any) => {

                for (let i = 0; i < variants.length; i++) {
                    variants[i].order = '';
                    for (let j = 0; j < variants[i].options.length; j++) {
                        variants[i].order += variants[i].options[j].value.order.toString();
                        if(j < variants[i].options.length - 1) {
                            variants[i].order += '.';
                        }
                    }
                }

                variants.sort((a, b) => {
                    if (a.order < b.order) { return -1; }
                    if (a.order > b.order) { return 1; }
                    return 0;
                });

                for (let i = 0; i < variants.length; i++) {
                    variants[i] = this.addSellPriceToProductVariant(variants[i]);
                }

                this.variants = variants;

            })
            .catch((error) => {
                console.error(error);
            });

        })
        .catch(() => {});

        this.observerService.subscribe('product_deleted|select_variant_modal', (product_id) => {
            if (this.product.id === product_id) {
                this.modalController.dismiss();
            }
        });

        this.observerService.subscribe('product_updated|select_variant_modal', (product) => {
            if (this.product.id === product.id) {
                this.product = product;
            }
        });

        this.observerService.subscribe('product_variant_added|select_variant_modal', (variant) => {
            if (variant.product_id === this.product.id) {
                variant = this.addSellPriceToProductVariant(variant);
                this.variants.push(variant);
                // NOTE: we don't re-sort at this point, as it would clause jank
            }
        });

        this.observerService.subscribe('product_variant_deleted|select_variant_modal', (variant) => {
            if (variant.product.id === this.product.id) {
                for (let i = 0; i < this.variants.length; i++) {
                    if (this.variants[i].id === variant.id) {
                        this.variants.splice(i, 1);
                        break;
                    }
                }
            }
        });

        this.observerService.subscribe('product_variant_updated|select_variant_modal', (variant) => {
            if (variant.product_id === this.product.id) {
                for (let i = 0; i < this.variants.length; i++) {
                    if (this.variants[i].id === variant['id']) {
                        variant = this.addSellPriceToProductVariant(variant);
                        this.variants[i] = variant;
                        break;
                    }
                }
            }
        });

        this.observerService.subscribe('product_variant_available_stock_updated|select_variant_modal', (variant) => {
            if (variant.product_id === this.product.id) {
                for (let i = 0; i < this.variants.length; i++) {
                    if (this.variants[i].id === variant['id']) {
                        this.variants[i].available_stock = variant.available_stock;
                        break;
                    }
                }
            }
        });

    }

    ngOnDestroy() {
        this.observerService.destroy("product_deleted|select_variant_modal");
        this.observerService.destroy("product_updated|select_variant_modal");
        this.observerService.destroy("product_variant_added|select_variant_modal");
        this.observerService.destroy("product_variant_deleted|select_variant_modal");
        this.observerService.destroy("product_variant_updated|select_variant_modal");
        this.observerService.destroy("product_variant_available_stock_updated|select_variant_modal");
    }

    addProductVariant(variant_id) {
        this.storageService.get("close_variant_modal").then((close_variant_modal) => {
            this.cartService.addProductVariantItem(variant_id).then(() => {
                if (close_variant_modal && close_variant_modal == true) {
                    this.modalController.dismiss();
                }
            })
            .catch((error) => {});
        })
        .catch((error) => {
            console.error(error)
        });
    }

    close() {
        this.modalController.dismiss();
    }

    addSellPriceToProductVariant(variant) {
        delete variant['sell_price'];
        delete variant['has_contract_price'];
        if(this.cart['person'] && this.cart["organisation"] && this.cart["organisation"].contract_prices[this.cart['currency'].id] && this.cart["organisation"].contract_prices[this.cart['currency'].id][variant.id]) {
            variant['sell_price'] = this.cart["organisation"].contract_prices[this.cart['currency'].id][variant.id].sell_price;
            variant['has_contract_price'] = true;
        }
        else {
            if (variant['prices']) {
                for (let i = 0; i < variant['prices'].length; i++) {
                    if (variant['prices'][i].currency.id === this.cart['currency'].id) {
                        for (let j = 0; j < variant['prices'][i].pricing_tiers.length; j++) {
                            if (variant['prices'][i].pricing_tiers[j].id === this.cart['pricing_tier'].id) {
                                variant['sell_price'] = variant['prices'][i].pricing_tiers[j].price;
                                break;
                            }
                        }
                        break;
                    }
                }
            }
        }
        return variant;
    }

    productVariantClicked(variant) {
        if(variant.track_stock == true) {
            let available_stock = variant.available_stock;
            this.cartService.get().then((cart) => {
                if(cart && cart['items'].length) {
                    for(let i = 0; i < cart['items'].length; i++) {
                        if(cart['items'][i].id == variant.id) {
                            available_stock = (available_stock - cart['items'][i].quantity);
                            break;
                        }
                    }
                }
                if(available_stock <= 0) {
                    if(!this.permissionService.isPermitted('pos-sell-oversell-item')) {
                        this.presentAlert('Insufficient permissions', "You don't have permission to oversell this item.");
                    }
                    else {
                        this.storageService.get("alert_if_overselling_item").then((alert_if_overselling_item) => {
                            if(alert_if_overselling_item == true) {
                                this.presentOversellingProductVariantConfirmation(variant.id);
                            }
                            else {
                                this.addProductVariant(variant.id);
                            }
                        })
                        .catch((error) => {
                            console.error(error)
                        });
                    }
                }
                else {
                    this.addProductVariant(variant.id);
                }
            })
            .catch((error) => {
                console.error(error)
            });
        }
        else {
            this.addProductVariant(variant.id);
        }
    }

    async presentOversellingProductVariantConfirmation(variant_id) {
        const alert = await this.alertController.create({
            header: 'No available stock left',
            message: 'Are you sure you want to oversell this item?',
            buttons: [
                {
                    text: 'Cancel',
                    handler: () => {
                        this.storageService.get("close_variant_modal").then((close_variant_modal) => {
                            if (close_variant_modal && close_variant_modal == true) {
                                this.modalController.dismiss();
                            }
                        })
                        .catch((error) => {
                            console.error(error)
                        });
                    }
                },
                {
                    text: 'Yes',
                    handler: () => {
                        this.addProductVariant(variant_id);
                    }
                }
            ],
        });
        await alert.present();
    }

    async presentAlert(header, message) {
        const alert = await this.alertController.create({
            header: header,
            message: message,
            buttons: ["OK"],
        });
        await alert.present();
    }

}
